import React, { useEffect, useState, useRef } from 'react';
import api, { analyticsAPI, handleAPIError, handleAPISuccess } from '../../services/api';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { useAuth } from '../../hooks/useAuth';
import { Trophy, Award, ShieldCheck, Users, CalendarDays, CheckCircle, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const Leaderboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);
  const [rows, setRows] = useState([]);
  const [month, setMonth] = useState('');
  const [yourRank, setYourRank] = useState(null);
  const [autoRenew, setAutoRenew] = useState(false);


  const lastRefreshRef = useRef(0);

  const loadData = async (options = { showSpinner: true }) => {
    try {
      if (options.showSpinner) setLoading(true);
      const [statusRes, boardRes] = await Promise.all([
        api.get('/analytics/leaderboard/status/', { params: { _t: Date.now() } }),
        api.get('/analytics/leaderboard/current/', { params: { _t: Date.now() } }),
      ]);
      if (statusRes?.data?.success) {
        setStatus(statusRes.data.data);
        setAutoRenew(Boolean(statusRes.data.data?.auto_renew));
      }
      if (boardRes?.data?.success) {
        const data = boardRes.data.data;
        setRows(Array.isArray(data.leaderboard) ? data.leaderboard : []);
        setMonth(data.month);
        setYourRank(data.your_rank);
      }
      // Record last refresh time after successful fetch
      lastRefreshRef.current = Date.now();
    } catch (err) {
      handleAPIError(err, 'Failed to load leaderboard');
    } finally {
      if (options.showSpinner) setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  // Determine if UK (LSE) market is open now (Mon–Fri, 08:00–16:30 Europe/London)
  const isLSEOpenNow = () => {
    try {
      const now = new Date();
      const weekdayParts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/London',
        weekday: 'short',
      }).formatToParts(now);
      const day = weekdayParts.find(p => p.type === 'weekday')?.value || '';
      const isWeekday = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].includes(day);
      if (!isWeekday) return false;

      const timeParts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/London',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).formatToParts(now);
      const h = parseInt(timeParts.find(p => p.type === 'hour')?.value || '0', 10);
      const m = parseInt(timeParts.find(p => p.type === 'minute')?.value || '0', 10);
      const minutes = h * 60 + m;
      return minutes >= (8 * 60) && minutes < (16 * 60 + 30);
    } catch {
      // Fallback: if timezone parsing fails, default to safe (closed)
      return false;
    }
  };

  // Auto-refresh hourly only during LSE hours; check every minute to hit the condition reliably
  useEffect(() => {
    const checkAndRefresh = () => {
      if (!isLSEOpenNow()) return;
      const now = Date.now();
      if (now - (lastRefreshRef.current || 0) >= 60 * 60 * 1000) {
        loadData({ showSpinner: false });
      }
    };

    // Run once on mount (after initial load sets lastRefreshRef)
    const id = setInterval(checkAndRefresh, 60 * 1000); // check every minute

    return () => {
      clearInterval(id);
    };
  }, []);



  const enroll = async () => {
    try {
      setSubmitting(true);
      const res = await analyticsAPI.enrollLeaderboard(autoRenew);
      if (res.data?.success) {
        handleAPISuccess('You are enrolled in the leaderboard');
        await loadData();
      }
    } catch (err) {
      handleAPIError(err, 'Enrollment failed');
    } finally {
      setSubmitting(false);
    }
  };

  const confirm = async () => {
    try {
      setSubmitting(true);
      const res = await analyticsAPI.confirmLeaderboard();
      if (res.data?.success) {
        handleAPISuccess('Participation confirmed for this month');
        await loadData();
      }
    } catch (err) {
      handleAPIError(err, 'Confirmation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const leave = async () => {
    try {
      setSubmitting(true);
      const res = await analyticsAPI.leaveLeaderboard();
      if (res.data?.success) {
        toast('You have left the leaderboard');
        await loadData();
      }
    } catch (err) {
      handleAPIError(err, 'Unable to leave leaderboard');
    } finally {
      setSubmitting(false);
    }
  };

  const EnrollCard = () => {
    const active = Boolean(status?.is_active);
    const needsConfirm = Boolean(status?.needs_confirmation);
    return (
      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-sm">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <Trophy className="w-6 h-6 text-amber-500" />
            <div>
              <div className="text-lg font-semibold">Monthly Leaderboard</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Ranked by monthly risk-adjusted returns after market close</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {active ? (
              <span className="inline-flex items-center gap-1.5 text-green-700 dark:text-green-300 text-sm font-medium">
                <ShieldCheck className="w-4 h-4" /> Enrolled
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-gray-600 dark:text-gray-300 text-sm">
                <Users className="w-4 h-4" /> Not enrolled
              </span>
            )}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          {!active && (
            <>
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" className="rounded" checked={autoRenew} onChange={(e) => setAutoRenew(e.target.checked)} />
                Auto-renew monthly
              </label>
              <button
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                onClick={enroll}
                disabled={submitting}
              >
                <Award className="w-4 h-4" /> Join Leaderboard
              </button>
            </>
          )}
          {active && needsConfirm && (
            <button
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              onClick={confirm}
              disabled={submitting}
            >
              <CheckCircle className="w-4 h-4" /> Confirm participation for {month}
            </button>
          )}
          {active && !needsConfirm && (
            <button
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
              onClick={leave}
              disabled={submitting}
            >
              <XCircle className="w-4 h-4" /> Leave Leaderboard
            </button>
          )}
        </div>

        <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
          Rankings refresh after UK market close (Mon-Fri). Your display name shows as first name + initial.
        </div>
      </div>
    );
  };

  const LeaderboardTable = () => {
    return (
      <div className="rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-blue-500" />
            <span className="font-semibold">{month || 'Current Month'}</span>
          </div>
          {yourRank && (
            <div className="text-sm text-gray-600 dark:text-gray-300">Your rank: <span className="font-semibold">#{yourRank}</span></div>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40">
              <tr>
                <th className="px-6 py-3 text-left font-medium text-gray-500 dark:text-gray-400">Rank</th>
                <th className="px-6 py-3 text-left font-medium text-gray-500 dark:text-gray-400">Trader</th>
                <th className="px-6 py-3 text-right font-medium text-gray-500 dark:text-gray-400">Return %</th>
                <th className="px-6 py-3 text-right font-medium text-gray-500 dark:text-gray-400">P&L (£)</th>
                <th className="px-6 py-3 text-right font-medium text-gray-500 dark:text-gray-400">Start Value</th>
                <th className="px-6 py-3 text-right font-medium text-gray-500 dark:text-gray-400">End Value</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">No rankings available yet. Check back after market close.</td>
                </tr>
              )}
              {rows.map((r, idx) => (
                <tr key={idx} className="border-t border-gray-100 dark:border-gray-700/50">
                  <td className="px-6 py-3 font-semibold">#{r.rank}</td>
                  <td className="px-6 py-3">{r.display_name}</td>
                  <td className={`px-6 py-3 text-right font-medium ${Number(r.return_percent) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {Number(r.return_percent).toFixed(2)}%
                  </td>
                  <td className="px-6 py-3 text-right">£{Number(r.return_absolute).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td className="px-6 py-3 text-right">£{Number(r.account_value_start).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td className="px-6 py-3 text-right">£{Number(r.account_value_end).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center"><LoadingSpinner size="lg" /></div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Leaderboard</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Compare your monthly trading performance with the community</p>
        </div>
        <div className="flex items-center gap-3" />
      </div>

      <EnrollCard />
      <LeaderboardTable />
    </div>
  );
};

export default Leaderboard;


